import { 
  collection, 
  query, 
  where, 
  getDocs, 
  addDoc, 
  updateDoc,
  serverTimestamp
} from 'firebase/firestore';
import { useFirebase } from '../context/FirebaseContext';
import {
  bureauOptions,
  mediumOptions,
  titelOptions,
  doelgroepOptions,
  uitingOptions
} from '../utils/staticData';

export const useFieldConfig = () => {
  const { db, currentUser } = useFirebase();

  // Generate default field configuration from staticData
  const getDefaultFieldConfig = () => {
    return {
      textNormalization: {
        lowercase: false,
        replaceSpaces: false,
        alphanumericOnly: false
      },
      fields: {
        Bureau: {
          enabled: true,
          displayName: 'Bureau',
          fieldType: 'text',
          options: [],
          gridOrder: 1,
          gridWidth: 1,
          required: true,
          autosuggest: false
        },
        Medium: {
          enabled: true,
          displayName: 'Type netwerk',
          fieldType: 'select',
          options: mediumOptions,
          gridOrder: 2,
          gridWidth: 1,
          required: true,
          autosuggest: false
        },
        Titel: {
          enabled: true,
          displayName: 'Naam netwerk',
          fieldType: 'select',
          options: titelOptions,
          gridOrder: 3,
          gridWidth: 1,
          required: true,
          autosuggest: false
        },
        Doelgroep: {
          enabled: true,
          displayName: 'Doelgroep',
          fieldType: 'select',
          options: doelgroepOptions,
          gridOrder: 4,
          gridWidth: 1.2,
          required: true,
          autosuggest: false
        },
        Uiting: {
          enabled: true,
          displayName: 'Type Uiting',
          fieldType: 'select',
          options: uitingOptions,
          gridOrder: 5,
          gridWidth: 1,
          required: true,
          autosuggest: false
        },
        Linknaam: {
          enabled: true,
          displayName: 'Naam uiting',
          fieldType: 'text',
          options: [],
          gridOrder: 6,
          gridWidth: 1,
          required: true,
          autosuggest: false
        },
        Campagne: {
          enabled: true,
          displayName: 'Campagne',
          fieldType: 'text',
          options: [],
          gridOrder: 7,
          gridWidth: 1.5,
          required: true,
          autosuggest: false
        },
        Datum: {
          enabled: true,
          displayName: 'Datum',
          fieldType: 'date',
          options: [],
          gridOrder: 8,
          gridWidth: 1,
          required: true,
          autosuggest: false
        },
        Url: {
          enabled: true,
          displayName: 'Url',
          fieldType: 'text',
          options: [],
          gridOrder: 9,
          gridWidth: 2,
          required: false,
          autosuggest: false
        }
      },
      linkTemplate: [
        { type: 'field', value: 'Url', transform: 'baseUrl' },
        { type: 'text', value: '?utm_source=' },
        { type: 'field', value: 'Bureau' },
        { type: 'text', value: '_' },
        { type: 'field', value: 'Titel' },
        { type: 'text', value: '_' },
        { type: 'field', value: 'Doelgroep' },
        { type: 'text', value: '&utm_campaign=' },
        { type: 'field', value: 'Campagne' },
        { type: 'text', value: '_' },
        { type: 'field', value: 'Datum', transform: 'removeSlashes' },
        { type: 'text', value: '&utm_medium=' },
        { type: 'field', value: 'Medium' },
        { type: 'text', value: '&utm_content=' },
        { type: 'field', value: 'Linknaam' },
        { type: 'text', value: '_' },
        { type: 'field', value: 'Uiting' },
        { type: 'field', value: 'Url', transform: 'fragment' }
      ]
    };
  };

  // Load field configuration for a project
  const loadFieldConfig = async (projectId) => {
    if (!projectId || projectId === '') {
      return getDefaultFieldConfig();
    }

    if (!currentUser) {
      return getDefaultFieldConfig();
    }

    try {
      const q = query(
        collection(db, 'fieldConfigs'),
        where('projectId', '==', projectId),
        where('userId', '==', currentUser.uid)
      );

      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        // No config found, return defaults
        return getDefaultFieldConfig();
      }

      // Return the first matching config
      const doc = querySnapshot.docs[0];
      const data = doc.data();
      
      return {
        textNormalization: data.textNormalization || getDefaultFieldConfig().textNormalization,
        fields: data.fields || getDefaultFieldConfig().fields,
        linkTemplate: data.linkTemplate || getDefaultFieldConfig().linkTemplate,
        _firestoreId: doc.id
      };
    } catch (error) {
      console.error('Error loading field config:', error);
      return getDefaultFieldConfig();
    }
  };

  // Save field configuration for a project
  const saveFieldConfig = async (projectId, config) => {
    try {
      if (!currentUser) {
        throw new Error('User must be logged in to save field configuration');
      }

      if (!projectId || projectId === '') {
        throw new Error('Project ID is required');
      }

      // Check if config already exists
      const q = query(
        collection(db, 'fieldConfigs'),
        where('projectId', '==', projectId),
        where('userId', '==', currentUser.uid)
      );

      const querySnapshot = await getDocs(q);

      const configData = {
        projectId: projectId,
        userId: currentUser.uid,
        textNormalization: config.textNormalization || getDefaultFieldConfig().textNormalization,
        fields: config.fields,
        linkTemplate: config.linkTemplate,
        updatedAt: serverTimestamp()
      };

      if (querySnapshot.empty) {
        // Create new config
        configData.createdAt = serverTimestamp();
        const docRef = await addDoc(collection(db, 'fieldConfigs'), configData);
        return { ...config, _firestoreId: docRef.id };
      } else {
        // Update existing config
        const docRef = querySnapshot.docs[0].ref;
        await updateDoc(docRef, configData);
        return { ...config, _firestoreId: querySnapshot.docs[0].id };
      }
    } catch (error) {
      console.error('Error saving field config:', error);
      throw error;
    }
  };

  return {
    loadFieldConfig,
    saveFieldConfig,
    getDefaultFieldConfig
  };
};

export default useFieldConfig;

